import { ModalConfirmation, ModalProps, useModal, withModalConditionalRender } from '@billy/design-system'
import { type ReactElement, useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import { useInvoicingSettingsHost } from '../../../../contexts/invoicingSettingsHostContext'

const UnsavedChangesModalComponent = ({ id }: ModalProps): ReactElement => {
  const { t } = useTranslation()
  const { close: closeUnsavedChangesModal } = useModal(id)
  const { applyPendingNavigation, cancelPendingNavigation, isProcessing, save, setIsDirty } = useInvoicingSettingsHost()

  // Discarding cannot stop an in-flight PUT, so the button is hidden while saving and the handler ignores
  // a click that still slips through.
  const handleDiscardClick = useCallback(() => {
    if (isProcessing) {
      return
    }

    setIsDirty(false)
    applyPendingNavigation()
  }, [applyPendingNavigation, isProcessing, setIsDirty])

  const handleSaveClick = useCallback(() => {
    save(() => {
      applyPendingNavigation()
      closeUnsavedChangesModal()
    })
  }, [applyPendingNavigation, closeUnsavedChangesModal, save])

  // `ModalConfirmation` is always closable, so X / Escape / overlay can dismiss this dialog while the
  // PUT is still running. That only takes the dialog away — the save keeps going and still has to
  // reach its destination — so the pending navigation is kept until `save` resolves and applies it.
  const handleUnsavedChangesModalClose = useCallback(() => {
    if (isProcessing) {
      return
    }

    cancelPendingNavigation()
  }, [cancelPendingNavigation, isProcessing])

  return (
    <ModalConfirmation
      cancelLabel={isProcessing ? undefined : t('discard_changes')}
      id={id}
      message={t('invoicing_settings.unsaved_changes_modal.message')}
      okLabel={t('save_changes')}
      okLoading={isProcessing}
      onCancel={handleDiscardClick}
      onClose={handleUnsavedChangesModalClose}
      onOk={handleSaveClick}
      title={t('unsaved_changes')}
    />
  )
}

export const UnsavedChangesModal = withModalConditionalRender(UnsavedChangesModalComponent)
